"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { MapPin, Calendar, ArrowRight, CircleCheck as CheckCircle2, ShieldCheck, UserCheck, TrendingUp, Search, Award, Sparkles } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Link } from "@/components/common/Link";
const trustHighlights = [{
  title: "1-on-1 Direct Expertise",
  description: "You work directly with me, Yassine Essaadi. No junior staff or agency middleman."
}, {
  title: "Bedford Geo-Targeting",
  description: "Custom local search strategies tailored specifically for Bedfordshire market intent."
}, {
  title: "Google Map Pack Dominance",
  description: "Data-driven Google Business Profile optimisation to convert local searchers."
}, {
  title: "Transparent & Honest ROI",
  description: "Clear deliverables, realistic growth forecasts, and detailed monthly progress tracking."
}];
const localMetrics = [{
  label: "Local Map Pack Visibility",
  value: "Top 3 Rank Target"
}, {
  label: "Direct Access",
  value: "100% Yassine"
}, {
  label: "Strategy Focus",
  value: "Bedford & Regional"
}];
export default function Localseo() {
  const shouldReduceMotion = useReducedMotion();
  const schemaData = {
    "@context": "https://schema.org",
    "@graph": [{
      "@type": "LocalBusiness",
      "@id": "https://ye-consultancy.com/#organization",
      "name": "YE Consultancy",
      "description": "Independent freelance SEO consultancy run by Yassine Essaadi",
      "url": "https://ye-consultancy.com/",
      "areaServed": "Bedford, Bedfordshire, UK"
    }, {
      "@type": "Person",
      "@id": "https://ye-consultancy.com/#person",
      "name": "Yassine Essaadi",
      "jobTitle": "Freelance SEO Consultant",
      "worksFor": {
        "@id": "https://ye-consultancy.com/#organization"
      }
    }, {
      "@type": "Service",
      "name": "Local SEO Services",
      "provider": {
        "@id": "https://ye-consultancy.com/#organization"
      },
      "areaServed": "Bedford"
    }, {
      "@type": "BreadcrumbList",
      "itemListElement": [{
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://ye-consultancy.com/"
      }, {
        "@type": "ListItem",
        "position": 2,
        "name": "Local SEO Consultant Bedford",
        "item": "https://ye-consultancy.com/LocalSeoConsultantBedford"
      }]
    }]
  };
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.05
      }
    }
  };
  const itemVariants = {
    hidden: shouldReduceMotion ? {
      opacity: 1,
      y: 0
    } : {
      opacity: 0,
      y: 18
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="localseohero" data-nav="dark" className="relative overflow-hidden bg-primary text-primary-foreground py-16 md:py-24 lg:py-28">
      {/* Schema.org Structured Data */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(schemaData)
    }} />

      {/* Decorative gradient blur circles */}
      <div className="pointer-events-none absolute -top-24 -left-24 size-96 rounded-full bg-accent/20 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-24 -right-24 size-96 rounded-full bg-secondary/15 blur-3xl" />
      
      {/* Background radial highlight */}
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-accent/10 via-transparent to-transparent opacity-60" />

      <div className="container relative mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12 lg:gap-8" variants={containerVariants} initial="hidden" animate="visible">
          {/* Left Column: Hero Content */}
          <div className="lg:col-span-7 flex flex-col items-start text-left">
            <motion.div variants={itemVariants} className="mb-4">
              <Badge variant="secondary" className="bg-secondary/90 text-secondary-foreground hover:bg-secondary font-medium px-3.5 py-1.5 text-xs sm:text-sm rounded-full gap-2 border border-secondary-foreground/10">
                <MapPin className="size-3.5 text-accent" />
                <span>Bedford &amp; Surrounding Areas • Freelance SEO Expert</span>
              </Badge>
            </motion.div>

            <motion.h1 variants={itemVariants} className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-primary-foreground leading-[1.12] mb-6">
              Local SEO Consultant in Bedford
            </motion.h1>

            <motion.p variants={itemVariants} className="font-sans text-base sm:text-lg text-primary-foreground/90 max-w-2xl leading-relaxed mb-8">
              As an independent local SEO consultant in Bedford, I help businesses improve their Google Search and Google Maps visibility to attract qualified local customers. Working directly with me ensures bespoke search strategies that generate more phone calls, enquiries, and sales.
            </motion.p>

            {/* CTAs */}
            <motion.div variants={itemVariants} className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 w-full sm:w-auto mb-10">
              <Button asChild size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90 shadow-md font-semibold text-base h-12 px-6 rounded-lg transition-transform active:scale-95">
                <Link to="/ContactPage">
                  <SafeHydrate><Calendar className="mr-2 size-5" /></SafeHydrate>
                  <span>Book a Free SEO Consultation</span>
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground font-semibold text-base h-12 px-6 rounded-lg transition-colors">
                <Link to="/SeoAuditsPage">
                  <span>Get a Free SEO Audit</span>
                  <ArrowRight className="ml-2 size-4" />
                </Link>
              </Button>
            </motion.div>

            {/* Direct Consultant Trust Badge */}
            <motion.div variants={itemVariants} className="inline-flex items-center gap-3 bg-primary-foreground/10 backdrop-blur-sm border border-primary-foreground/15 rounded-xl p-3.5 pr-5">
              <div className="relative">
                <SafeHydrate><Avatar className="size-11 border-2 border-accent">
                  <AvatarImage src="https://mighty-wolf.10web.cloud/wp-content/uploads/2026/08/SEO-Consultant-Freelancer.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/Seo-Consultant.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/Seo-Consultant.png-wvc-sizes" alt="Yassine Essaadi - Local SEO Consultant Bedford" />
                  <AvatarFallback className="bg-secondary text-secondary-foreground font-bold">
                    YE
                  </AvatarFallback>
                </Avatar></SafeHydrate>
                <span className="absolute -bottom-1 -right-1 flex size-4 items-center justify-center rounded-full bg-accent text-accent-foreground text-[11.5px]">
                  <CheckCircle2 className="size-3" />
                </span>
              </div>
              <div>
                <p className="font-serif text-sm font-semibold text-primary-foreground leading-snug">
                  You work directly with Yassine Essaadi
                </p>
                <p className="font-sans text-xs text-primary-foreground/80">
                  Independent Freelance SEO Consultant • No Account Managers
                </p>
              </div>
            </motion.div>
          </div>

          {/* Right Column: Local SEO Feature Card */}
          <motion.div variants={itemVariants} className="lg:col-span-5 w-full">
            <div className="bg-card text-card-foreground rounded-2xl p-6 sm:p-8 shadow-xl border border-border/80 relative overflow-hidden">
              <div className="absolute top-0 right-0 bg-accent text-accent-foreground text-xs font-semibold px-3 py-1 rounded-bl-lg flex items-center gap-1">
                <Sparkles className="size-3" />
                <span>Bedford Focus</span>
              </div>

              <div className="mb-6">
                <div className="flex items-center gap-2 text-primary font-serif font-bold text-lg mb-1">
                  <Search className="size-5 text-accent" />
                  <span>Local Search Blueprint</span>
                </div>
                <p className="text-xs text-muted-foreground">
                  Data-backed strategy designed specifically for Bedfordshire businesses.
                </p>
              </div>

              {/* Quick Metrics */}
              <div className="grid grid-cols-3 gap-2 bg-muted/60 rounded-xl p-3 mb-6 text-center">
                {localMetrics.map((metric, i) => <div key={metric.label} data-index={i} className="flex flex-col items-center">
                    <span className="font-serif font-bold text-xs sm:text-sm text-foreground">
                      {metric.value}
                    </span>
                    <span className="font-sans text-[11.5px] text-muted-foreground leading-tight mt-0.5">
                      {metric.label}
                    </span>
                  </div>)}
              </div>

              {/* Value propositions */}
              <div className="space-y-4 mb-6">
                {trustHighlights.map((highlight, i) => <div key={highlight.title} data-index={i} className="flex items-start gap-3">
                    <div className="mt-0.5 rounded-full bg-accent/15 p-1 text-accent shrink-0">
                      <ShieldCheck className="size-4" />
                    </div>
                    <div>
                      <h4 className="font-serif text-sm font-semibold text-foreground leading-snug">
                        {highlight.title}
                      </h4>
                      <p className="font-sans text-xs text-muted-foreground leading-relaxed">
                        {highlight.description}
                      </p>
                    </div>
                  </div>)}
              </div>

              {/* Local SEO Link */}
              <div className="border-t border-border pt-4 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Award className="size-4 text-accent" />
                  <span className="font-sans text-xs font-medium text-foreground">
                    Tailored SEO Strategy &amp; Honest Advice
                  </span>
                </div>
                <Link to="/LocalSEOPage" className="text-xs font-semibold text-primary hover:text-primary/80 flex items-center gap-1 transition-colors">
                  <span>Learn More</span>
                  <ArrowRight className="size-3" />
                </Link>
              </div>
            </div>
          </motion.div>
        </motion.div>

        {/* Bottom Trust Indicators Bar */}
        <motion.div variants={itemVariants} className="mt-16 pt-8 border-t border-primary-foreground/15 grid grid-cols-2 md:grid-cols-4 gap-6 text-center sm:text-left">
          <div className="flex flex-col sm:flex-row items-center gap-3">
            <UserCheck className="size-5 text-accent shrink-0" />
            <div>
              <p className="font-serif text-sm font-semibold text-primary-foreground">Direct Access</p>
              <p className="font-sans text-xs text-primary-foreground/75">Work 100% with Yassine</p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-3">
            <TrendingUp className="size-5 text-accent shrink-0" />
            <div>
              <p className="font-serif text-sm font-semibold text-primary-foreground">Sustainable Growth</p>
              <p className="font-sans text-xs text-primary-foreground/75">White-hat local SEO</p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-3">
            <MapPin className="size-5 text-accent shrink-0" />
            <div>
              <p className="font-serif text-sm font-semibold text-primary-foreground">Bedford Expertise</p>
              <p className="font-sans text-xs text-primary-foreground/75">Targeted regional search</p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-3">
            <ShieldCheck className="size-5 text-accent shrink-0" />
            <div>
              <p className="font-serif text-sm font-semibold text-primary-foreground">Clear Deliverables</p>
              <p className="font-sans text-xs text-primary-foreground/75">No hidden agency fees</p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}